// hooks/useKycByCusId.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckKycResult,
  checkKycStatus,
  submitKyc,
} from "../repositories/kyc.repository";
import { KycFormInput, KycModel } from "../types/kyc";
import { useCheckoutFlowStore } from "../store/useCheckoutFlowStore";

export function useKyc(cusId: string | null | undefined) {
  const queryClient = useQueryClient();
  const setKycInfo = useCheckoutFlowStore((s) => s.setKycInfo);
  const setKycVerified = useCheckoutFlowStore((s) => s.setKycVerified);
  const contactInfo = useCheckoutFlowStore((s) => s.contactInfo);

  const canFetch = Boolean(cusId);

  const {
    data: kycStatus,
    isPending: isKycStatusLoading,
    isError: isKycStatusError,
    error: kycStatusError,
  } = useQuery<CheckKycResult>({
    queryKey: ["kyc", cusId],
    enabled: canFetch,
    queryFn: async () => {
      const res = await checkKycStatus(cusId as string);
      setKycInfo(res.profile as KycModel);
      setKycVerified(res.isKycVerified); // or based on res.status
      return res;
    },
  });

  const {
    mutateAsync: submitKycAsync,
    mutate: submitKycMutate,
    isPending: isSubmitting,
    isError: isSubmitError,
    error: submitKycError,
  } = useMutation<KycModel, Error, KycFormInput>({
    mutationFn: (form) => {
      const msisdn = `${contactInfo.countryCode}${contactInfo.phoneNumber}`;
      return submitKyc(form, cusId as string, msisdn);
    },
    onSuccess: (res) => {
      setKycInfo(res);
      setKycVerified(true);
      queryClient.setQueryData<CheckKycResult>(["kyc", cusId], {
        isKycVerified: true,
        profile: res,
      });
    },
  });

  return {
    // read
    submitKycMutate,
    kycStatus,
    isKycStatusLoading,
    isKycStatusError,
    kycStatusError,
    // write
    submitKycAsync,
    isSubmitting,
    isSubmitError,
    submitKycError,
  };
}
